import { useState, useEffect, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import { UserContext, API } from "@/App";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  User, Wallet, History, ArrowUpRight, ArrowDownLeft,
  Clock, Trophy, CreditCard, Check, Loader2, Gift, Shield,
  TrendingUp, Zap
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const PACKAGES = [
  { id: "starter", name: "Starter", amount: 500, credits: 500, bonus: 0, popular: false },
  { id: "popular", name: "Popular", amount: 1000, credits: 1000, bonus: 100, popular: true },
  { id: "value", name: "Value", amount: 2500, credits: 2500, bonus: 500, popular: false },
  { id: "premium", name: "Premium", amount: 5000, credits: 5000, bonus: 1500, popular: false },
  { id: "vip", name: "VIP", amount: 10000, credits: 10000, bonus: 4000, popular: false },
];

export const ProfilePage = () => {
  const { user, refreshBalance, token } = useContext(UserContext);
  const [searchParams] = useSearchParams();
  const [withdrawAmount, setWithdrawAmount] = useState(100);
  const [transactions, setTransactions] = useState([]);
  const [bets, setBets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  useEffect(() => {
    fetchData();
    
    // Check for payment return
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    
    if (sessionId && status === 'success') {
      pollPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      toast.error("Payment cancelled");
    }
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [txRes, betsRes, statsRes] = await Promise.all([
        axios.get(`${API}/wallet/transactions`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/games/history`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/user/stats`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null }))
      ]);
      setTransactions(txRes.data.transactions || []);
      setBets(betsRes.data.bets || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Fetch error:", error);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error("Payment verification timed out. Please check your email for confirmation.");
      return;
    }

    setProcessingPayment(true);

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        toast.success(response.data.message || "Credits added to your wallet!");
        await refreshBalance();
        fetchData();
        setProcessingPayment(false);
        // Clear URL params
        window.history.replaceState({}, '', '/profile');
        return;
      } else if (response.data.status === 'expired') {
        toast.error("Payment session expired. Please try again.");
        setProcessingPayment(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Payment status error:", error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  const handleBuyCredits = async (pkg) => {
    setSelectedPackage(pkg.id);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        package_id: pkg.id,
        origin_url: window.location.origin
      }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });

      // Redirect to Stripe
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initiate payment");
      setLoading(false);
      setSelectedPackage(null);
    }
  };

  const handleWithdraw = async () => {
    if (withdrawAmount <= 0 || withdrawAmount > (user?.balance || 0)) {
      toast.error("Invalid withdrawal amount");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/wallet/withdraw`, {
        amount: withdrawAmount,
        transaction_type: "withdraw"
      }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`₹${withdrawAmount} withdrawal requested!`);
      await refreshBalance();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Withdrawal failed");
    } finally {
      setLoading(false);
    }
  };

  const totalWon = stats?.total_won || 0;
  const totalWagered = stats?.total_wagered || 0;
  const winRate = stats?.win_rate || 0;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        {/* Processing Payment Overlay */}
        {processingPayment && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center">
            <div className="text-center p-8 rounded-2xl bg-[#1A1B1F] border border-[#00E701]/30">
              <Loader2 className="w-12 h-12 text-[#00E701] animate-spin mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Processing Payment</h3>
              <p className="text-[#A1A1AA]">Please wait while we verify your payment...</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-16 h-16 rounded-full border-2 border-[#00E701]" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#FFD600] to-[#FF6B00] flex items-center justify-center text-black text-2xl font-bold">
              {user?.name?.charAt(0) || 'U'}
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold text-white font-['Rajdhani']" data-testid="profile-name">
              {user?.name}
            </h1>
            <p className="text-[#A1A1AA]">{user?.email}</p>
            <div className="flex items-center gap-2 mt-1">
              <Shield className="w-4 h-4 text-[#FFD600]" />
              <span className="text-[#FFD600] text-sm font-medium">VIP Level {user?.vip_level || 1}</span>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="p-4 rounded-xl bg-gradient-to-br from-[#00E701]/20 to-transparent border border-[#00E701]/20">
            <div className="flex items-center gap-2 text-[#A1A1AA] text-sm mb-2">
              <Wallet className="w-4 h-4" />
              Balance
            </div>
            <p className="text-2xl font-bold text-[#00E701] font-mono" data-testid="profile-balance">
              ₹{(user?.balance || 0).toLocaleString('en-IN')}
            </p>
          </div>
          <div className="p-4 rounded-xl bg-gradient-to-br from-[#FFD600]/20 to-transparent border border-[#FFD600]/20">
            <div className="flex items-center gap-2 text-[#A1A1AA] text-sm mb-2">
              <Trophy className="w-4 h-4" />
              Total Won
            </div>
            <p className="text-2xl font-bold text-[#FFD600] font-mono">
              ₹{totalWon.toLocaleString('en-IN')}
            </p>
          </div>
          <div className="p-4 rounded-xl bg-gradient-to-br from-[#007AFF]/20 to-transparent border border-[#007AFF]/20">
            <div className="flex items-center gap-2 text-[#A1A1AA] text-sm mb-2">
              <TrendingUp className="w-4 h-4" />
              Total Wagered
            </div>
            <p className="text-2xl font-bold text-[#007AFF] font-mono">
              ₹{totalWagered.toLocaleString('en-IN')}
            </p>
          </div>
          <div className="p-4 rounded-xl bg-gradient-to-br from-[#FF007A]/20 to-transparent border border-[#FF007A]/20">
            <div className="flex items-center gap-2 text-[#A1A1AA] text-sm mb-2">
              <Zap className="w-4 h-4" />
              Win Rate
            </div>
            <p className="text-2xl font-bold text-[#FF007A] font-mono">
              {winRate.toFixed(1)}%
            </p>
          </div>
        </div>

        <Tabs defaultValue="buy" className="space-y-6">
          <TabsList className="bg-[#1A1B1F] border border-white/[0.08]">
            <TabsTrigger value="buy" className="data-[state=active]:bg-[#00E701] data-[state=active]:text-black">
              <CreditCard className="w-4 h-4 mr-2" /> Buy Credits
            </TabsTrigger>
            <TabsTrigger value="withdraw" className="data-[state=active]:bg-[#00E701] data-[state=active]:text-black">
              <ArrowUpRight className="w-4 h-4 mr-2" /> Withdraw
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-[#00E701] data-[state=active]:text-black">
              <History className="w-4 h-4 mr-2" /> History
            </TabsTrigger>
          </TabsList>

          {/* Buy Credits Tab */}
          <TabsContent value="buy">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {PACKAGES.map((pkg) => (
                <div
                  key={pkg.id}
                  className={`relative p-6 rounded-xl border transition-all ${
                    pkg.popular 
                      ? 'bg-gradient-to-br from-[#FFD600]/20 to-transparent border-[#FFD600]/50' 
                      : 'bg-[#1A1B1F] border-white/10 hover:border-[#00E701]/30'
                  }`}
                >
                  {pkg.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#FFD600] text-black text-xs font-bold">
                      MOST POPULAR
                    </div>
                  )}
                  
                  <h3 className="text-xl font-bold text-white font-['Rajdhani'] mb-2">{pkg.name}</h3>
                  
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-white font-mono">₹{pkg.amount.toLocaleString()}</span>
                  </div>
                  
                  <div className="space-y-2 mb-6">
                    <div className="flex items-center gap-2 text-[#A1A1AA]">
                      <Check className="w-4 h-4 text-[#00E701]" />
                      <span>₹{pkg.credits.toLocaleString()} credits</span>
                    </div>
                    {pkg.bonus > 0 && (
                      <div className="flex items-center gap-2 text-[#FFD600]">
                        <Gift className="w-4 h-4" />
                        <span>+₹{pkg.bonus.toLocaleString()} bonus!</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-[#A1A1AA]">
                      <Shield className="w-4 h-4 text-[#007AFF]" />
                      <span>Instant delivery</span>
                    </div>
                  </div>
                  
                  <Button
                    onClick={() => handleBuyCredits(pkg)}
                    disabled={loading}
                    className={`w-full h-12 font-bold ${
                      pkg.popular 
                        ? 'bg-[#FFD600] hover:bg-[#E6C200] text-black' 
                        : 'bg-[#00E701] hover:bg-[#00C201] text-black'
                    }`}
                    data-testid={`buy-${pkg.id}-btn`}
                  >
                    {loading && selectedPackage === pkg.id ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Buy Now
                      </>
                    )}
                  </Button>
                </div>
              ))}
            </div>
            
            <div className="mt-6 p-4 rounded-lg bg-white/5 flex items-center gap-3">
              <Shield className="w-6 h-6 text-[#007AFF]" />
              <div>
                <p className="text-white font-medium">Secure Payments by Stripe</p>
                <p className="text-[#A1A1AA] text-sm">All transactions are encrypted and secure. We accept all major cards & UPI.</p>
              </div>
            </div>
          </TabsContent>

          {/* Withdraw Tab */}
          <TabsContent value="withdraw">
            <div className="max-w-md mx-auto p-6 rounded-xl bg-[#1A1B1F] border border-white/[0.08]">
              <div className="flex items-center gap-2 mb-6">
                <ArrowUpRight className="w-5 h-5 text-[#FF3B30]" />
                <h3 className="text-lg font-bold text-white font-['Rajdhani']">Withdraw Funds</h3>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="text-[#A1A1AA] text-sm mb-2 block">Amount (₹)</label>
                  <Input
                    type="number"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(Number(e.target.value))}
                    max={user?.balance || 0}
                    className="h-12 bg-black/20 border-white/10 text-white font-mono text-lg"
                    data-testid="withdraw-input"
                  />
                  <p className="text-[#52525B] text-xs mt-2">
                    Available: ₹{(user?.balance || 0).toLocaleString()}
                  </p>
                </div>
                
                <div className="flex gap-2">
                  {[25, 50, 75, 100].map(pct => (
                    <Button
                      key={pct}
                      variant="outline"
                      size="sm"
                      onClick={() => setWithdrawAmount(Math.floor((user?.balance || 0) * pct / 100))}
                      className="flex-1 border-white/10 text-white/60 hover:text-white"
                    >
                      {pct}%
                    </Button>
                  ))}
                </div>
                
                <Button
                  onClick={handleWithdraw}
                  disabled={loading || withdrawAmount > (user?.balance || 0) || withdrawAmount <= 0}
                  className="w-full h-12 bg-[#FF3B30] hover:bg-[#E63329] text-white font-bold"
                  data-testid="withdraw-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    `Withdraw ₹${withdrawAmount.toLocaleString()}`
                  )}
                </Button>
                
                <p className="text-[#52525B] text-xs text-center">
                  Withdrawals are processed within 24 hours
                </p>
              </div>
            </div>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Transactions */}
              <div className="p-6 rounded-xl bg-[#1A1B1F] border border-white/[0.08]">
                <h3 className="text-lg font-bold text-white font-['Rajdhani'] mb-4 flex items-center gap-2">
                  <Wallet className="w-5 h-5" /> Transactions
                </h3>
                
                {transactions.length > 0 ? (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {transactions.slice(0, 20).map((tx, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            tx.type === 'deposit' ? 'bg-[#00E701]/20' : 
                            tx.type === 'withdraw' ? 'bg-[#FF3B30]/20' :
                            tx.type === 'win' ? 'bg-[#FFD600]/20' : 'bg-white/10'
                          }`}>
                            {tx.type === 'deposit' || tx.type === 'win' ? (
                              <ArrowDownLeft className={`w-4 h-4 ${tx.type === 'deposit' ? 'text-[#00E701]' : 'text-[#FFD600]'}`} />
                            ) : (
                              <ArrowUpRight className="w-4 h-4 text-[#FF3B30]" />
                            )}
                          </div>
                          <div>
                            <p className="text-white capitalize">{tx.type}</p>
                            <p className="text-[#52525B] text-xs">
                              {new Date(tx.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <span className={`font-mono font-bold ${
                          tx.amount > 0 ? 'text-[#00E701]' : 'text-[#FF3B30]'
                        }`}>
                          {tx.amount > 0 ? '+' : ''}₹{Math.abs(tx.amount).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#52525B] text-center py-8">No transactions yet</p>
                )}
              </div>

              {/* Bet History */}
              <div className="p-6 rounded-xl bg-[#1A1B1F] border border-white/[0.08]">
                <h3 className="text-lg font-bold text-white font-['Rajdhani'] mb-4 flex items-center gap-2">
                  <History className="w-5 h-5" /> Bet History
                </h3>
                
                {bets.length > 0 ? (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {bets.slice(0, 20).map((bet, idx) => (
                      <div key={idx} className={`flex items-center justify-between p-3 rounded-lg ${
                        bet.status === 'won' ? 'bg-[#00E701]/10' : 
                        bet.status === 'lost' ? 'bg-[#FF3B30]/10' : 'bg-white/5'
                      }`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${
                            bet.status === 'won' ? 'bg-[#00E701]' : 
                            bet.status === 'lost' ? 'bg-[#FF3B30]' : 'bg-[#FFD600]'
                          }`} />
                          <div>
                            <p className="text-white capitalize">{bet.game_type}</p>
                            <p className="text-[#52525B] text-xs">
                              {new Date(bet.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-[#A1A1AA] text-sm">₹{bet.bet_amount}</p>
                          <p className={`font-mono font-bold ${
                            bet.status === 'won' ? 'text-[#00E701]' : 'text-[#FF3B30]'
                          }`}>
                            {bet.status === 'won' ? `+₹${bet.winnings?.toFixed(0)}` : `-₹${bet.bet_amount}`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#52525B] text-center py-8">No bets yet</p>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};
